import React, { useState, useRef, useEffect } from "react";
import ViewerActions from "../../actions/ViewerActions";
import ViewerStore from "../../stores/ViewerStore";
import * as Inovx from '@shared';
import { DialogComponent } from "@syncfusion/ej2-react-popups";
import AssemblyLibraryStore from "../../stores/AssemblyLibraryStore";
import ViewerConstants from "../../constants/ViewerConstants";
import ColorPickerWithNoColorCheckbox from "../../components/knowledgebook/ColorPickerWithNoColorCheckbox";
import useAsyncActionLock from '@shared/hooks/useAsyncActionLock';
const SystemActions = Inovx.Shared.System.Actions;
const UrlUtilities = Inovx.Shared.Common.UrlUtilities;
const InovxControls = Inovx.Shared.Controls;
var SystemStore = Inovx.Shared.System.Store;

declare var InovxLoader: any;

// ---------- Types ----------
interface EditAssemblyProps {
    assembly: IAssembly;
    onClose: () => void
}
interface AssemblyFormErrorProps {
    Name: boolean;
}

interface AssemblyFormProps {
    Name: string;
    Color?: number | null;
}

// ---------- Component ----------
const EditAssemblyForm = React.forwardRef((props: EditAssemblyProps, ref) => {
    const selectedAssembly: string | null = ViewerStore.getSelectedAssembly();
    const selectedAssemblyObj: IAssembly | undefined = props.assembly;
    const [assemblyForm, setAssemblyForm] = useState<AssemblyFormProps | null>(null);
    const defaultErrors: AssemblyFormErrorProps = { Name: false}
    const [formErrors, setFormErrors] = useState<AssemblyFormErrorProps>(defaultErrors);
    const [isActionInProgress, setActionInProgress] = useState<boolean>(false);
    const [hasChange, setHasChange] = useState<boolean>(false);

    const dialogInstance = useRef<DialogComponent | null>(null);

    const getIsActionInProgress = () => {
        return SystemStore.isActionInProgress(ViewerConstants.ASSEMBLY_INSTANCE_UPDATE) ||
            SystemStore.isActionInProgress(ViewerConstants.ASSEMBLY_INSTANCE_UPDATE);
    }
    const systemChange = () => {
        setActionInProgress(getIsActionInProgress())
    }

    useEffect(() => {
        SystemStore.addChangeListener(systemChange);

        return () => {
            SystemStore.removeChangeListener(systemChange);
        };
    }, []);

    useEffect(() => {
        setAssemblyForm({
            Name: props.assembly.Name,
            Color: props.assembly.Color ?? null,
        });
    }, [props.assembly]);

    const handleInputChange = (e: any) => {
        const decimalPattern = /^[-+]?\d*\.?\d*$/;
        const { name, value } = e.target;
        if (name.includes('.')) {
            // Handle nested keys
            const [parentKey, childKey] = name.split('.');
            if (decimalPattern.test(value) || value === '') {
                setAssemblyForm((prevForm: any) => ({
                    ...prevForm,
                    [parentKey]: {
                        ...prevForm[parentKey],
                        [childKey]: value === '' ? '' : value,
                    },
                }));
            }
        } else {
            // Handle top-level keys
            setAssemblyForm((prevForm) => ({
                ...prevForm,
                [name]: value,
            }));
        }
        setHasChange(true);
    }

    const closeEditModal = (args: any) => {
        if (!hasChange) {
            props.onClose();
        } else {
            args.cancel = true;
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: () => saveAssemblyInstance(),
                cancelCallback: () => props.onClose(),
            });
        }
    };

    const isFormValid = () => {
        let errors = { ...formErrors };
        let flag = true;
        if (!assemblyForm.Name || assemblyForm.Name === '') {
            errors.Name = true;
            flag = false;
        } else {
            errors.Name = false;
        }
        setFormErrors(errors)
        return flag;
    }

    const saveAssemblyInstance = async () => {
        if (isFormValid()) {
            const colorChanged = assemblyForm.Color !== (selectedAssemblyObj.Color ?? null);

            await ViewerActions.updateAssemblyInstance(selectedAssembly, assemblyForm.Name, assemblyForm.Color);

            props.onClose();
            if (colorChanged) {
                ViewerActions.resetAssemblySelection(false);
            }
            ViewerActions.resetDisplayMode();
            setHasChange(false);
        }
    }

    const saveAssemblyAction = useAsyncActionLock(saveAssemblyInstance);

    const resetAssemblyForm = () => {

        setAssemblyForm({
            Name: selectedAssemblyObj.Name,
            Color: selectedAssemblyObj.Color ?? null,
        })
        setFormErrors(defaultErrors)
        setHasChange(false);
    }

    const assemblyHeader = () => (
        <div>
            <div className="e-icon-settings dlg-template">{InovxLoader.t("facility:menu.editAssembly")}</div>
        </div>
    );
    var modalFooter = () => {
        return (<div>
            <div className="d-flex j-cc pb-10px">
                <div className="d-flex j-cc a-ic">
                    {(isActionInProgress || saveAssemblyAction.isInFlight) && <div className="message mr-5px">{InovxLoader.t('facility:form.saving')}</div>} <a id="facilityApp-save-btn" aria-disabled={saveAssemblyAction.isInFlight} onClick={saveAssemblyAction.isInFlight ? undefined : () => void saveAssemblyAction.run()} className={`btn btn-primary${saveAssemblyAction.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t("facility:preferences.saveButton")}</a>
                </div>
                <div className="ml-1">
                    <a id="facilityApp-cancel-btn" aria-disabled={saveAssemblyAction.isInFlight} onClick={saveAssemblyAction.isInFlight ? undefined : () => resetAssemblyForm()} className={`btn btn-default${saveAssemblyAction.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t("facility:preferences.resetButton")}</a>
                </div>
            </div>
        </div>);
    }

    return (
        <>
            {assemblyForm && (
                <DialogComponent
                    ref={dialogInstance}
                    allowDragging={true}
                    width="450px"
                    header={assemblyHeader}
                    footerTemplate={modalFooter}
                    isModal={true}
                    zIndex={110000}
                    showCloseIcon={true}
                    visible={true}
                    beforeClose={closeEditModal}
                >
                    <div className="dialogContent">
                        <InovxControls.FormField title={InovxLoader.t('facility:viewer.instanceName')} inputName='X' className={formErrors.Name ? 'has-error' : ''}>
                            <input type="text" onChange={handleInputChange} value={assemblyForm.Name} className="form-control" name="Name" placeholder={InovxLoader.t('facility:viewer.enterInstanceName')} autoComplete="off" />
                        </InovxControls.FormField>

                        <InovxControls.FormField title={InovxLoader.t('facility:action.colorAssembly')} inputName='Color' className='assembly-color-form-field'>
                            <ColorPickerWithNoColorCheckbox
                                id={`assembly-color-${selectedAssemblyObj.ID}`}
                                value={assemblyForm.Color}
                                noColorLabel={InovxLoader.t('facility:action.noColorDisplayAssemblyInherent')}
                                useNativeTrigger={true}
                                onChange={color => {
                                    setAssemblyForm(previous => previous ? { ...previous, Color: color } : previous);
                                    setHasChange(true);
                                }}
                            />
                        </InovxControls.FormField>

                        {/* <div className="ctm-accordion mt-3px">
                            <div className="a-item">
                                <div className="a-header" onClick={() => toggleExpandedItems('Position')}>
                                    <span className={`expander-icon`}>
                                        <img
                                            src={`${UrlUtilities.getBaseApplicationUrl()}/images/icons/Arrow.png`}
                                            alt={''}
                                            className={`arrow-expander ${expandedItems.Position ? 'expanded' : ''}`}
                                        />
                                    </span>
                                    <span className="a-title">{InovxLoader.t('facility:viewer.position')}</span>
                                </div>
                                {expandedItems.Position && <div className="row d-flex ml-10px">
                                    <div className="col-md-12 field-col">
                                        <InovxControls.FormField title={`North: `} className={`${formErrors.Position.Z ? 'has-error' : ''} d-flex a-ic`} inputName='Position.Z'>
                                            <input type="text" className="form-control w-50" onChange={handleInputChange} value={(assemblyForm.Position.Z)} placeholder={InovxLoader.t('facility:form.enterValue')} name='Position.Z' />
                                            <div className="d-flex j-cc a-ic"><span className="add-on">{ViewerStore.getUnitInformation().Unit}</span></div>
                                        </InovxControls.FormField>

                                        <InovxControls.FormField title={`West: `} className={`${formErrors.Position.X ? 'has-error' : ''} d-flex a-ic`} inputName='Position.X'>
                                            <input type="text" className="form-control w-50" onChange={handleInputChange} value={(assemblyForm.Position.X)} placeholder={InovxLoader.t('facility:form.enterValue')} name='Position.X' />
                                            <div className="d-flex j-cc a-ic"><span className="add-on">{ViewerStore.getUnitInformation().Unit}</span></div>
                                        </InovxControls.FormField>

                                        <InovxControls.FormField title={`Up: `} className={`${formErrors.Position.Y ? 'has-error' : ''} d-flex a-ic`} inputName='Position.Y'>
                                            <input type="text" className="form-control w-50" onChange={handleInputChange} value={(assemblyForm.Position.Y)} placeholder={InovxLoader.t('facility:form.enterValue')} name='Position.Y' />
                                            <div className="d-flex j-cc a-ic"><span className="add-on">{ViewerStore.getUnitInformation().Unit}</span></div>
                                        </InovxControls.FormField>
                                    </div>
                                </div>}
                            </div>
                            <div className="a-item">
                                <div className="a-header" onClick={() => toggleExpandedItems('Dimension')}>
                                    <span className={`expander-icon`}>
                                        <img
                                            src={`${UrlUtilities.getBaseApplicationUrl()}/images/icons/Arrow.png`}
                                            alt={''}
                                            className={`arrow-expander ${expandedItems.Dimension ? 'expanded' : ''}`}
                                        />
                                    </span>
                                    <span className="a-title">{InovxLoader.t('facility:form.dimension')}</span>
                                </div>
                                {expandedItems.Dimension && <div className="row d-flex ml-10px">
                                    <div className="col-md-12 field-col">
                                        <InovxControls.FormField title={`${InovxLoader.t('facility:form.length')}: `} className={`${formErrors.Dimension.Length ? 'has-error' : ''} d-flex a-ic`} inputName='Dimension.Length'>
                                            <input type="text" className="form-control w-50" onChange={handleInputChange} value={(assemblyForm.Dimension.Length)} disabled={selectedAssemblyTemplateObj.Scalable ? false : true} placeholder={InovxLoader.t('facility:form.enterValue')} name='Dimension.Length' />
                                            <div className="d-flex j-cc a-ic"><span className="add-on">{ViewerStore.getUnitInformation().Unit}</span></div>
                                        </InovxControls.FormField>

                                        <InovxControls.FormField title={`${InovxLoader.t('facility:form.width')}: `} className={`${formErrors.Dimension.Width ? 'has-error' : ''} d-flex a-ic`} inputName='Dimension.Width'>
                                            <input type="text" className="form-control w-50" onChange={handleInputChange} value={(assemblyForm.Dimension.Width)} disabled={selectedAssemblyTemplateObj.Scalable ? false : true} placeholder={InovxLoader.t('facility:form.enterValue')} name='Dimension.Width' />
                                            <div className="d-flex j-cc a-ic"><span className="add-on">{ViewerStore.getUnitInformation().Unit}</span></div>
                                        </InovxControls.FormField>

                                        <InovxControls.FormField title={`${InovxLoader.t('facility:form.height')}: `} className={`${formErrors.Dimension.Height ? 'has-error' : ''} d-flex a-ic`} inputName='Dimension.Height'>
                                            <input type="text" className="form-control w-50" onChange={handleInputChange} value={(assemblyForm.Dimension.Height)} disabled={selectedAssemblyTemplateObj.Scalable ? false : true} placeholder={InovxLoader.t('facility:form.enterValue')} name='Dimension.Height' />
                                            <div className="d-flex j-cc a-ic"><span className="add-on">{ViewerStore.getUnitInformation().Unit}</span></div>
                                        </InovxControls.FormField>
                                    </div>
                                </div>}
                            </div>
                        </div> */}

                    </div>
                </DialogComponent>
            )}
        </>
    );
});

export default EditAssemblyForm;
